import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, CalendarDays, FileText, Loader2, Plus, Receipt, SendHorizonal, Wallet, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router';
import { DriverBottomNav } from './DriverBottomNav';
import { expenseService } from '../services/expenseService';
import { driverService } from '../services/driverService';
import { driverIncidentReportService } from '../services/driverIncidentReportService';
import { documentService } from '../services/documentService';
import { expenseCategoryService } from '../services/expenseCategoryService';

const LEAVE_TYPES = [
  { value: 'Yıllık İzin', label: 'Yıllık İzin' },
  { value: 'Mazeret İzni', label: 'Mazeret İzni' },
  { value: 'Sağlık İzni', label: 'Sağlık İzni' },
  { value: 'Ücretsiz İzin', label: 'Ücretsiz İzin' },
];

const INCIDENT_TYPES = [
  { value: 'accident', label: 'Kaza' },
  { value: 'vehicle-general-breakdown', label: 'Araç Genel Arıza' },
  { value: 'seyir-mobile', label: 'Seyir Mobil' },
  { value: 'general-maintenance', label: 'Genel Bakım' },
  { value: 'damage', label: 'Hasar' },
];

const resolveFileUrl = (filePath?: string | null) => {
  if (!filePath) return '#';
  if (/^https?:\/\//i.test(filePath)) return filePath;
  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${apiBase}${filePath.startsWith('/') ? filePath : `/${filePath}`}`;
};

const parseOcrMetadata = (value: any) => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

const getCategorySuggestionTone = (confidence?: number) => {
  if ((confidence || 0) >= 80) {
    return {
      box: 'border-emerald-200 bg-emerald-50',
      title: 'text-emerald-700',
      text: 'text-emerald-900',
      action: 'bg-white text-emerald-700 border border-emerald-200',
      suggested: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      level: 'Yüksek güven',
    };
  }
  if ((confidence || 0) >= 60) {
    return {
      box: 'border-amber-200 bg-amber-50',
      title: 'text-amber-700',
      text: 'text-amber-900',
      action: 'bg-white text-amber-700 border border-amber-200',
      suggested: 'bg-amber-100 text-amber-800 border-amber-300',
      level: 'Orta güven',
    };
  }
  return {
    box: 'border-rose-200 bg-rose-50',
    title: 'text-rose-700',
    text: 'text-rose-900',
    action: 'bg-white text-rose-700 border border-rose-200',
    suggested: 'bg-rose-100 text-rose-800 border-rose-300',
    level: 'Düşük güven',
  };
};

export function DriverMobilePersonnelCenter() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [driverId, setDriverId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'expenses' | 'leaves' | 'incidents'>('expenses');
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [leaves, setLeaves] = useState<any[]>([]);
  const [incidentReports, setIncidentReports] = useState<any[]>([]);
  const [leaveBalance, setLeaveBalance] = useState<any | null>(null);
  const [expenseModal, setExpenseModal] = useState(false);
  const [leaveModal, setLeaveModal] = useState(false);
  const [incidentModal, setIncidentModal] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const [savingLeave, setSavingLeave] = useState(false);
  const [savingIncident, setSavingIncident] = useState(false);
  const [expenseReceipt, setExpenseReceipt] = useState<File | null>(null);
  const [expenseCategories, setExpenseCategories] = useState<Array<{ value: string; label: string }>>([]);
  const [expenseForm, setExpenseForm] = useState({ category: '', amount: '', date: new Date().toISOString().split('T')[0], description: '' });
  const [leaveForm, setLeaveForm] = useState({ leaveType: '', startDate: new Date().toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0], reason: '', notes: '' });
  const [incidentForm, setIncidentForm] = useState({
    reportType: 'vehicle-general-breakdown',
    vehicleKind: 'tractor',
    title: '',
    description: '',
    incidentDate: new Date().toISOString().slice(0, 16),
    city: '',
    district: '',
    workStartAt: '',
    workEndAt: '',
    estimatedCost: '',
    invoiceAmount: '',
    invoiceNumber: '',
  });
  const [incidentInvoiceFile, setIncidentInvoiceFile] = useState<File | null>(null);
  const [incidentImageFiles, setIncidentImageFiles] = useState<File[]>([]);
  const [receiptOcr, setReceiptOcr] = useState<any | null>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrCategoryAccepted, setOcrCategoryAccepted] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem('driverMobileUser');
    if (!raw) {
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    const parsed = JSON.parse(raw);
    setUser(parsed);
    initialize(parsed);
  }, [navigate]);

  const initialize = async (currentUser: any) => {
    try {
      setLoading(true);
      const driverResponse = await driverService.getDriverByUserId(currentUser.id);
      const driver = driverResponse.data || driverResponse;
      const resolvedDriverId = String(driver?.id || currentUser?.driverId || '');
      setDriverId(resolvedDriverId);

      const [expensesRes, leavesRes, categoryRes, leaveBalanceRes, incidentRes] = await Promise.all([
        expenseService.getExpenses({ submittedBy: String(currentUser.id) }),
        resolvedDriverId ? driverService.getLeaveRequests({ driverId: resolvedDriverId }) : Promise.resolve({ data: [] }),
        expenseCategoryService.getCategories(),
        resolvedDriverId ? driverService.getDriverLeaveBalance(resolvedDriverId) : Promise.resolve({ data: { summary: null } }),
        resolvedDriverId ? driverIncidentReportService.getReports({ driverId: resolvedDriverId }) : Promise.resolve({ data: [] }),
      ]);

      setExpenseCategories((categoryRes.data || categoryRes || []).map((item: any) => ({ value: item.code, label: item.label })));

      setExpenses((expensesRes.data || expensesRes || []).map((item: any) => ({
        id: String(item.id),
        category: item.category,
        amount: Number(item.amount || 0),
        description: item.description || '',
        expenseDate: item.expense_date || item.date || '',
        status: item.status || 'pending',
        receiptPath: item.receipt_path || item.receiptUrl || '',
        notes: item.notes || '',
        suggestedCategory: item.suggested_category || item.suggestedCategory || '',
        ocrData: parseOcrMetadata(item.ocr_metadata) || item.ocrData || null,
      })));

      setLeaves((leavesRes.data || leavesRes || []).map((item: any) => ({
        id: String(item.id),
        leaveType: item.leave_type || item.leaveType,
        startDate: item.start_date || item.startDate,
        endDate: item.end_date || item.endDate,
        days: Number(item.days || 0),
        reason: item.reason || '',
        status: item.status || 'Beklemede',
        notes: item.notes || item.rejection_reason || '',
      })));

      setLeaveBalance(leaveBalanceRes?.data?.summary || null);
      setIncidentReports(Array.isArray(incidentRes?.data) ? incidentRes.data : []);
    } catch (error) {
      console.error('Personnel center load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const expenseStats = useMemo(() => ({
    pending: expenses.filter((item) => item.status === 'pending').length,
    approved: expenses.filter((item) => item.status === 'approved' || item.status === 'paid').length,
    paid: expenses.filter((item) => item.status === 'paid').length,
    total: expenses.reduce((sum, item) => sum + Number(item.amount || 0), 0),
  }), [expenses]);

  const leaveStats = useMemo(() => ({
    pending: leaves.filter((item) => item.status === 'Beklemede').length,
    approved: leaves.filter((item) => item.status === 'Onaylandı').length,
    rejected: leaves.filter((item) => item.status === 'Reddedildi').length,
  }), [leaves]);

  const incidentStats = useMemo(() => ({
    newCount: incidentReports.filter((item) => item.workflowStatus === 'todo').length,
    seenCount: incidentReports.filter((item) => item.workflowStatus === 'in_progress' || item.workflowStatus === 'service_notified').length,
    resolvedCount: incidentReports.filter((item) => item.workflowStatus === 'completed').length,
  }), [incidentReports]);

  const submitExpense = async () => {
    if (!user || !expenseForm.category || !expenseForm.amount || !expenseForm.description) {
      alert('Lütfen zorunlu alanları doldurun');
      return;
    }

    try {
      setSavingExpense(true);
      let receiptPath = '';
      if (expenseReceipt) {
        const formData = new FormData();
        formData.append('file', expenseReceipt);
        formData.append('entityType', 'expense');
        formData.append('entityId', String(user.id));
        formData.append('documentType', 'expense-receipt');
        formData.append('name', expenseReceipt.name);
        const upload = await documentService.uploadDocument(formData);
        receiptPath = upload.data?.filePath || '';
      }

      await expenseService.createExpense({
        submittedBy: user.id,
        category: expenseForm.category,
        description: expenseForm.description,
        amount: Number(expenseForm.amount),
        expenseDate: expenseForm.date,
        receiptPath,
        ocrData: receiptOcr,
        suggestedCategory: receiptOcr?.suggestedCategory || null,
      });

      setExpenseModal(false);
      setExpenseReceipt(null);
      setReceiptOcr(null);
      setOcrCategoryAccepted(false);
      setExpenseForm({ category: '', amount: '', date: new Date().toISOString().split('T')[0], description: '' });
      await initialize(user);
    } catch (error) {
      console.error('Expense submit failed:', error);
      alert('Masraf kaydı gönderilemedi');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleReceiptChange = async (file: File | null) => {
    setExpenseReceipt(file);
    setReceiptOcr(null);
    setOcrCategoryAccepted(false);
    if (!file) return;

    try {
      setOcrLoading(true);
      const response = await documentService.ocrReceipt(file);
      if (response.success && response.data) {
        setReceiptOcr(response.data);
        setExpenseForm((current) => ({
          ...current,
          amount: response.data?.total ? String(response.data.total) : current.amount,
          category: response.data?.suggestedCategory || current.category,
          date: response.data?.date
            ? (() => {
                const raw = response.data.date.replace(/\./g, '-').replace(/\//g, '-');
                if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
                  const [day, month, year] = raw.split('-');
                  return `${year}-${month}-${day}`;
                }
                return current.date;
              })()
            : current.date,
          description: response.data?.merchant ? `${response.data.merchant} - ${current.description || 'Fiş masrafı'}` : current.description,
        }));
      }
    } catch (error) {
      console.error('Receipt OCR failed:', error);
    } finally {
      setOcrLoading(false);
    }
  };

  const getLeaveCalendarDays = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const result = [];
    for (let day = 1; day <= end.getDate(); day += 1) {
      const current = new Date(today.getFullYear(), today.getMonth(), day);
      const iso = current.toISOString().split('T')[0];
      const leave = leaves.find((item) => {
        const startDate = new Date(item.startDate);
        const endDate = new Date(item.endDate);
        return current >= startDate && current <= endDate;
      });
      result.push({ iso, day, leave });
    }
    return result;
  };

  const submitLeave = async () => {
    if (!driverId || !leaveForm.leaveType || !leaveForm.startDate || !leaveForm.endDate || !leaveForm.reason) {
      alert(!driverId ? 'Sürücü kaydı bulunamadı, izin talebi oluşturulamıyor' : 'Lütfen zorunlu alanları doldurun');
      return;
    }

    const start = new Date(leaveForm.startDate);
    const end = new Date(leaveForm.endDate);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    try {
      setSavingLeave(true);
      await driverService.createLeaveRequest({
        driverId,
        driverName: user?.name || user?.full_name || user?.fullName || 'Sürücü',
        leaveType: leaveForm.leaveType,
        startDate: leaveForm.startDate,
        endDate: leaveForm.endDate,
        days,
        reason: leaveForm.reason,
        requestDate: new Date().toISOString().split('T')[0],
        notes: leaveForm.notes,
      });

      setLeaveModal(false);
      setLeaveForm({ leaveType: '', startDate: new Date().toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0], reason: '', notes: '' });
      await initialize(user);
    } catch (error) {
      console.error('Leave submit failed:', error);
      alert('İzin talebi gönderilemedi');
    } finally {
      setSavingLeave(false);
    }
  };

  const submitIncident = async () => {
    if (!incidentForm.description.trim()) {
      alert('Lütfen açıklama girin');
      return;
    }

    try {
      setSavingIncident(true);
      const reportResponse: any = await driverIncidentReportService.createReport({
        reportType: incidentForm.reportType,
        description: incidentForm.description.trim(),
        title: incidentForm.title.trim(),
        workflowStatus: 'todo',
        vehicleKind: incidentForm.vehicleKind,
        incidentDate: incidentForm.incidentDate || null,
        city: incidentForm.city || null,
        district: incidentForm.district || null,
        workStartAt: incidentForm.workStartAt || null,
        workEndAt: incidentForm.workEndAt || null,
        estimatedCost: Number(incidentForm.estimatedCost || 0),
        invoiceAmount: Number(incidentForm.invoiceAmount || 0),
        invoiceNumber: incidentForm.invoiceNumber || null,
      });
      if (!reportResponse?.success || !reportResponse?.data?.id) {
        throw new Error(reportResponse?.message || 'Kayıt oluşturulamadı');
      }

      const reportId = String(reportResponse.data.id);

      let invoiceDocumentId: string | null = null;
      if (incidentInvoiceFile) {
        const formData = new FormData();
        formData.append('file', incidentInvoiceFile);
        formData.append('entityType', 'driver_incident_report');
        formData.append('entityId', reportId);
        formData.append('documentType', 'incident-invoice');
        formData.append('name', incidentInvoiceFile.name);
        const upload = await documentService.uploadDocument(formData);
        invoiceDocumentId = upload?.data?.id ? String(upload.data.id) : null;
      }

      for (const file of incidentImageFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityType', 'driver_incident_report');
        formData.append('entityId', reportId);
        formData.append('documentType', 'incident-image');
        formData.append('name', file.name);
        await documentService.uploadDocument(formData);
      }

      if (invoiceDocumentId) {
        await driverIncidentReportService.updateReport(reportId, { invoiceDocumentId });
      }

      setIncidentModal(false);
      setIncidentForm({ reportType: 'vehicle-general-breakdown', vehicleKind: 'tractor', title: '', description: '', incidentDate: new Date().toISOString().slice(0, 16), city: '', district: '', workStartAt: '', workEndAt: '', estimatedCost: '', invoiceAmount: '', invoiceNumber: '' });
      setIncidentInvoiceFile(null);
      setIncidentImageFiles([]);
      await initialize(user);
    } catch (error) {
      console.error('Incident report submit failed:', error);
      alert('Arıza/kaza kaydı gönderilemedi');
    } finally {
      setSavingIncident(false);
    }
  };

  const getExpenseStatusText = (status: string) => {
    if (status === 'approved') return 'Onaylandı';
    if (status === 'rejected') return 'Reddedildi';
    if (status === 'paid') return 'Ödendi';
    return 'Beklemede';
  };

  if (loading) {
    return <div className="safe-area-page min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  const suggestionTone = getCategorySuggestionTone(receiptOcr?.categoryConfidence);

    return (
    <div className="safe-area-page min-h-screen overflow-x-hidden bg-slate-50 pb-[calc(8rem+env(safe-area-inset-bottom,0px))] dark:bg-[#0b0b0b]">
      <div className="border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-sky-900 dark:to-cyan-800 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => navigate(-1)} className="rounded-lg p-2 text-slate-800 hover:bg-slate-100 dark:text-white dark:hover:bg-white/10"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Masraf ve İzinler</h1>
          <div className="w-9" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/10"><p className="text-xs text-slate-600 dark:text-sky-100">Bekleyen Masraf</p><p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{expenseStats.pending}</p></div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/10"><p className="text-xs text-slate-600 dark:text-sky-100">Onaylı Masraf</p><p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{expenseStats.approved}</p></div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/10"><p className="text-xs text-slate-600 dark:text-sky-100">Bekleyen İzin</p><p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{leaveStats.pending}</p></div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/10"><p className="text-xs text-slate-600 dark:text-sky-100">Toplam Masraf</p><p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{expenseStats.total.toLocaleString('tr-TR')} ₺</p></div>
        </div>
      </div>

      <div className="p-4 pb-10 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <button onClick={() => setActiveTab('expenses')} className={`rounded-2xl py-3 text-sm font-medium ${activeTab === 'expenses' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 border border-slate-200'}`}>Masraflar</button>
          <button onClick={() => setActiveTab('leaves')} className={`rounded-2xl py-3 text-sm font-medium ${activeTab === 'leaves' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 border border-slate-200'}`}>İzin Talepleri</button>
          <button onClick={() => setActiveTab('incidents')} className={`rounded-2xl py-3 text-sm font-medium ${activeTab === 'incidents' ? 'bg-rose-600 text-white' : 'bg-white text-slate-700 border border-slate-200'}`}>Arıza/Kaza</button>
        </div>

        {activeTab === 'expenses' && (
          <>
            <button onClick={() => setExpenseModal(true)} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 text-white py-3 font-medium"><Plus className="w-4 h-4" />Yeni Masraf Yükle</button>
            <div className="space-y-3">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs text-emerald-800">
                Yeni masraf ekleyebilir, altta daha once girdigin masraflarin onay / red / odendi durumlarini gorebilirsin.
              </div>
              {expenses.map((expense) => (
                <div key={expense.id} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{expenseCategories.find((item) => item.value === expense.category)?.label || expense.category}</div>
                      <div className="text-xs text-slate-500 mt-1">{expense.expenseDate ? new Date(expense.expenseDate).toLocaleDateString('tr-TR') : '-'}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${expense.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : expense.status === 'rejected' ? 'bg-rose-50 text-rose-700' : expense.status === 'paid' ? 'bg-sky-50 text-sky-700' : 'bg-blue-50 text-blue-700'}`}>{getExpenseStatusText(expense.status)}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-3">{expense.description}</p>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="text-emerald-700 font-semibold">{Number(expense.amount || 0).toLocaleString('tr-TR')} ₺</div>
                    {expense.receiptPath ? <a href={resolveFileUrl(expense.receiptPath)} target="_blank" rel="noreferrer" className="text-sm text-blue-600">Fişi Gör</a> : null}
                  </div>
                  {expense.notes ? <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">Not: {expense.notes}</div> : null}
                  {expense.ocrData?.suggestedCategory ? <div className="mt-2 text-xs text-sky-700">OCR kategori önerisi: {expenseCategories.find((item) => item.value === expense.ocrData.suggestedCategory)?.label || expense.ocrData.suggestedCategory}</div> : null}
                </div>
              ))}
              {expenses.length === 0 && <div className="bg-white rounded-2xl p-5 text-sm text-slate-500">Henüz masraf kaydı yok.</div>}
            </div>
          </>
        )}

        {activeTab === 'leaves' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-xs text-emerald-700">Kalan İzin Hakkı</p>
                <p className="mt-1 text-2xl font-bold text-emerald-900">{Number(leaveBalance?.remainingDays || 0).toLocaleString('tr-TR')} gün</p>
              </div>
              <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
                <p className="text-xs text-sky-700">Kullanılan İzin</p>
                <p className="mt-1 text-2xl font-bold text-sky-900">{Number(leaveBalance?.usedDays || 0).toLocaleString('tr-TR')} gün</p>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              <div className="flex items-center justify-between gap-3">
                <span>Eklenen Hak</span>
                <span className="font-semibold text-slate-900">{Number(leaveBalance?.addedDays || 0).toLocaleString('tr-TR')} gün</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <span>Toplam Düşüm</span>
                <span className="font-semibold text-slate-900">{Number(leaveBalance?.deductedDays || 0).toLocaleString('tr-TR')} gün</span>
              </div>
            </div>
            <button onClick={() => setLeaveModal(true)} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-violet-600 text-white py-3 font-medium"><Plus className="w-4 h-4" />Yeni İzin Talebi</button>
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3"><CalendarDays className="w-4 h-4 text-violet-600" /><p className="text-sm font-semibold text-slate-900">Bu Ay İzin Takvimi</p></div>
              <div className="grid grid-cols-7 gap-2 text-center">
                {getLeaveCalendarDays().map((item) => (
                  <div key={item.iso} className={`rounded-xl px-2 py-3 text-xs border ${item.leave ? item.leave.status === 'Onaylandı' ? 'bg-green-50 border-green-200 text-green-700' : item.leave.status === 'Reddedildi' ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-slate-50 border-slate-100 text-slate-500'}`}>
                    <div className="font-semibold">{item.day}</div>
                    <div className="mt-1 truncate">{item.leave ? item.leave.leaveType : '-'}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              {leaves.map((leave) => (
                <div key={leave.id} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{leave.leaveType}</div>
                      <div className="text-xs text-slate-500 mt-1">{new Date(leave.startDate).toLocaleDateString('tr-TR')} - {new Date(leave.endDate).toLocaleDateString('tr-TR')}</div>
                    </div>
                    <span className="px-2 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-medium">{leave.status}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-3">{leave.reason}</p>
                  {leave.notes ? <div className="mt-3 text-xs text-slate-500 bg-slate-50 rounded-xl px-3 py-2">{leave.notes}</div> : null}
                </div>
              ))}
              {leaves.length === 0 && <div className="bg-white rounded-2xl p-5 text-sm text-slate-500">Henüz izin talebi yok.</div>}
            </div>
          </>
        )}

        {activeTab === 'incidents' && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4"><p className="text-xs text-rose-700">Yeni</p><p className="mt-1 text-2xl font-bold text-rose-900">{incidentStats.newCount}</p></div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4"><p className="text-xs text-amber-700">Görüldü</p><p className="mt-1 text-2xl font-bold text-amber-900">{incidentStats.seenCount}</p></div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4"><p className="text-xs text-emerald-700">Çözüldü</p><p className="mt-1 text-2xl font-bold text-emerald-900">{incidentStats.resolvedCount}</p></div>
            </div>
            <button onClick={() => setIncidentModal(true)} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 text-white py-3 font-medium"><Plus className="w-4 h-4" />Yeni Arıza / Kaza Kaydı</button>
            <div className="space-y-3">
              {incidentReports.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{item.title}</div>
                      <div className="text-xs text-slate-500 mt-1">{item.reportType === 'accident' ? 'Kaza' : item.reportType === 'vehicle-general-breakdown' ? 'Araç Genel Arıza' : item.reportType === 'seyir-mobile' ? 'Seyir Mobil' : item.reportType === 'general-maintenance' ? 'Genel Bakım' : item.reportType === 'damage' ? 'Hasar' : 'Arıza'} • {item.vehiclePlate || '-'}{item.trailerPlate ? ` / ${item.trailerPlate}` : ''}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.workflowStatus === 'completed' ? 'bg-emerald-100 text-emerald-700' : item.workflowStatus === 'service_notified' ? 'bg-sky-100 text-sky-700' : item.workflowStatus === 'in_progress' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>{item.workflowStatus === 'completed' ? 'Tamamlandı' : item.workflowStatus === 'service_notified' ? 'Servise Bildirildi' : item.workflowStatus === 'in_progress' ? 'Yapılıyor' : 'Yapılacak'}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-3">{item.description}</p>
                  <div className="mt-3 text-xs text-slate-500">{item.createdAt ? new Date(item.createdAt).toLocaleString('tr-TR') : '-'}</div>
                </div>
              ))}
              {incidentReports.length === 0 && <div className="bg-white rounded-2xl p-5 text-sm text-slate-500">Henüz arıza/kaza kaydı yok.</div>}
            </div>
          </>
        )}
      </div>

      {expenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50 p-3">
          <div className="bg-white rounded-t-3xl w-full max-h-[calc(100vh-1rem)] overflow-y-auto p-4 space-y-4 pb-[calc(9rem+env(safe-area-inset-bottom,0px))]">
            <div className="flex items-center justify-between sticky top-0 bg-white py-1"><h3 className="text-lg font-bold text-slate-900">Yeni Masraf</h3><button onClick={() => setExpenseModal(false)} className="text-sm text-slate-500">Kapat</button></div>
            <select value={expenseForm.category} onChange={(e) => setExpenseForm((current) => ({ ...current, category: e.target.value }))} className="w-full border rounded-xl px-3 py-3"><option value="">Masraf türü seçin</option>{expenseCategories.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
            <input value={expenseForm.amount} onChange={(e) => setExpenseForm((current) => ({ ...current, amount: e.target.value }))} type="number" step="0.01" placeholder="Tutar" className="w-full border rounded-xl px-3 py-3" />
            <input value={expenseForm.date} onChange={(e) => setExpenseForm((current) => ({ ...current, date: e.target.value }))} type="date" className="w-full border rounded-xl px-3 py-3" />
            <textarea value={expenseForm.description} onChange={(e) => setExpenseForm((current) => ({ ...current, description: e.target.value }))} rows={4} placeholder="Masraf açıklaması" className="w-full border rounded-xl px-3 py-3 resize-none" />
            {receiptOcr?.suggestedCategory && (
              <div className={`rounded-2xl border p-3 space-y-3 ${suggestionTone.box}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className={`text-xs font-medium ${suggestionTone.title}`}>OCR kategori önerisi</p>
                    <p className={`text-sm font-semibold mt-1 ${suggestionTone.text}`}>
                      {expenseCategories.find((item) => item.value === receiptOcr.suggestedCategory)?.label || receiptOcr.suggestedCategory}
                    </p>
                    <p className={`text-[11px] mt-1 ${suggestionTone.title}`}>{suggestionTone.level} - %{Math.round(receiptOcr.categoryConfidence || 0)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setExpenseForm((current) => ({ ...current, category: receiptOcr.suggestedCategory }));
                      setOcrCategoryAccepted(true);
                    }}
                    className={`px-3 py-2 rounded-xl text-xs font-medium ${ocrCategoryAccepted ? 'bg-blue-600 text-white' : suggestionTone.action}`}
                  >
                    {ocrCategoryAccepted ? 'Öneri Uygulandı' : 'Kategoriyi Uygula'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {expenseCategories.map((item) => {
                    const isSelected = expenseForm.category === item.value;
                    const isSuggested = receiptOcr.suggestedCategory === item.value;
                    return (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => setExpenseForm((current) => ({ ...current, category: item.value }))}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${isSelected ? 'bg-blue-600 text-white border-blue-600' : isSuggested ? suggestionTone.suggested : 'bg-white text-slate-600 border-slate-200'}`}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <label className="block border-2 border-dashed rounded-2xl border-slate-300 p-4 text-center text-sm text-slate-600 cursor-pointer">Fiş / fatura seç<input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleReceiptChange(e.target.files?.[0] || null)} />{expenseReceipt ? <div className="mt-2 text-emerald-700 font-medium">{expenseReceipt.name}</div> : null}</label>
            {(ocrLoading || receiptOcr) && (
              <div className="rounded-2xl bg-sky-50 border border-sky-200 p-3 text-xs text-sky-800 space-y-3">
                <div className="flex items-center gap-2 font-medium mb-2"><Sparkles className="w-4 h-4" />Fiş OCR</div>
                {ocrLoading ? <div>Fiş okunuyor...</div> : (
                  <>
                    <div className="space-y-1">
                      <div>İşyeri: {receiptOcr?.merchant || '-'}</div>
                      <div>Tutar: {receiptOcr?.total ? `${Number(receiptOcr.total).toLocaleString('tr-TR')} ₺` : '-'}</div>
                      <div>Tarih: {receiptOcr?.date || '-'}</div>
                      <div>VKN: {receiptOcr?.taxNumber || '-'}</div>
                      <div>Güven: %{receiptOcr?.confidence ? Math.round(receiptOcr.confidence) : 0}</div>
                      <div>Kategori Önerisi: {expenseCategories.find((item) => item.value === receiptOcr?.suggestedCategory)?.label || receiptOcr?.suggestedCategory || '-'}</div>
                    </div>
                    {(receiptOcr?.summary?.subtotal || receiptOcr?.summary?.vat || receiptOcr?.summary?.grandTotal) ? (
                      <div className="rounded-xl bg-white/80 border border-sky-100 p-3">
                        <p className="font-medium text-sky-900 mb-2">Fiş Özeti</p>
                        <div className="space-y-1 text-[11px]">
                          <div>Ara Toplam: {receiptOcr?.summary?.subtotal != null ? `${Number(receiptOcr.summary.subtotal).toLocaleString('tr-TR')} ₺` : '-'}</div>
                          <div>KDV: {receiptOcr?.summary?.vat != null ? `${Number(receiptOcr.summary.vat).toLocaleString('tr-TR')} ₺` : '-'}</div>
                          <div>Genel Toplam: {receiptOcr?.summary?.grandTotal != null ? `${Number(receiptOcr.summary.grandTotal).toLocaleString('tr-TR')} ₺` : '-'}</div>
                        </div>
                      </div>
                    ) : null}
                    {receiptOcr?.items?.length ? (
                      <div className="rounded-xl bg-white/80 border border-sky-100 p-3">
                        <p className="font-medium text-sky-900 mb-2">Satır Satır Ürün / Tutar</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {receiptOcr.items.map((item: any, index: number) => (
                            <div key={`${item.raw}-${index}`} className="flex items-start justify-between gap-3 text-[11px] border-b border-sky-50 pb-2 last:border-b-0 last:pb-0">
                              <span className="text-sky-950">{item.description}</span>
                              <span className="font-medium text-sky-900 whitespace-nowrap">{Number(item.amount).toLocaleString('tr-TR')} ₺</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <div className="rounded-xl bg-white/80 border border-sky-100 p-3">
                      <p className="font-medium text-sky-900 mb-2">Tam OCR İçeriği</p>
                      <div className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words text-[11px] leading-5 text-sky-950">
                        {receiptOcr?.rawText || receiptOcr?.lines?.join('\n') || '-'}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <button onClick={submitExpense} disabled={savingExpense} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white py-3 font-medium disabled:bg-emerald-300 sticky bottom-0">{savingExpense ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}Masrafı Onaya Gönder</button>
          </div>
        </div>
      )}

      {leaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50 p-3">
          <div className="bg-white rounded-t-3xl w-full max-h-[85vh] overflow-y-auto p-4 space-y-4 pb-28">
            <div className="flex items-center justify-between sticky top-0 bg-white py-1"><h3 className="text-lg font-bold text-slate-900">Yeni İzin Talebi</h3><button onClick={() => setLeaveModal(false)} className="text-sm text-slate-500">Kapat</button></div>
            <select value={leaveForm.leaveType} onChange={(e) => setLeaveForm((current) => ({ ...current, leaveType: e.target.value }))} className="w-full border rounded-xl px-3 py-3"><option value="">İzin türü seçin</option>{LEAVE_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
            <div className="grid grid-cols-2 gap-3"><input value={leaveForm.startDate} onChange={(e) => setLeaveForm((current) => ({ ...current, startDate: e.target.value }))} type="date" className="w-full border rounded-xl px-3 py-3" /><input value={leaveForm.endDate} onChange={(e) => setLeaveForm((current) => ({ ...current, endDate: e.target.value }))} type="date" className="w-full border rounded-xl px-3 py-3" /></div>
            <textarea value={leaveForm.reason} onChange={(e) => setLeaveForm((current) => ({ ...current, reason: e.target.value }))} rows={4} placeholder="İzin sebebi" className="w-full border rounded-xl px-3 py-3 resize-none" />
            <textarea value={leaveForm.notes} onChange={(e) => setLeaveForm((current) => ({ ...current, notes: e.target.value }))} rows={3} placeholder="Ek not" className="w-full border rounded-xl px-3 py-3 resize-none" />
            <button onClick={submitLeave} disabled={savingLeave} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 text-white py-3 font-medium disabled:bg-violet-300 sticky bottom-0">{savingLeave ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizonal className="w-4 h-4" />}İzin Talebini Gönder</button>
          </div>
        </div>
      )}

      {incidentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50 p-3">
          <div className="bg-white rounded-t-3xl w-full max-h-[85vh] overflow-y-auto p-4 space-y-4 pb-28">
            <div className="flex items-center justify-between sticky top-0 bg-white py-1"><h3 className="text-lg font-bold text-slate-900">Yeni Arıza / Kaza Kaydı</h3><button onClick={() => setIncidentModal(false)} className="text-sm text-slate-500">Kapat</button></div>
            <div className="grid grid-cols-2 gap-3">
              <select value={incidentForm.vehicleKind} onChange={(e) => setIncidentForm((current) => ({ ...current, vehicleKind: e.target.value }))} className="w-full border rounded-xl px-3 py-3"><option value="tractor">Çekici</option><option value="trailer">Dorse</option></select>
              <select value={incidentForm.reportType} onChange={(e) => setIncidentForm((current) => ({ ...current, reportType: e.target.value }))} className="w-full border rounded-xl px-3 py-3">{INCIDENT_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
            </div>
            <input value={incidentForm.title} onChange={(e) => setIncidentForm((current) => ({ ...current, title: e.target.value }))} placeholder="Kısa başlık (opsiyonel)" className="w-full border rounded-xl px-3 py-3" />
            <input type="datetime-local" value={incidentForm.incidentDate} onChange={(e) => setIncidentForm((current) => ({ ...current, incidentDate: e.target.value }))} className="w-full border rounded-xl px-3 py-3" />
            <div className="grid grid-cols-2 gap-3">
              <input value={incidentForm.city} onChange={(e) => setIncidentForm((current) => ({ ...current, city: e.target.value }))} placeholder="Arıza/Kaza İli" className="w-full border rounded-xl px-3 py-3" />
              <input value={incidentForm.district} onChange={(e) => setIncidentForm((current) => ({ ...current, district: e.target.value }))} placeholder="Arıza/Kaza İlçesi" className="w-full border rounded-xl px-3 py-3" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="datetime-local" value={incidentForm.workStartAt} onChange={(e) => setIncidentForm((current) => ({ ...current, workStartAt: e.target.value }))} className="w-full border rounded-xl px-3 py-3" />
              <input type="datetime-local" value={incidentForm.workEndAt} onChange={(e) => setIncidentForm((current) => ({ ...current, workEndAt: e.target.value }))} className="w-full border rounded-xl px-3 py-3" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" step="0.01" value={incidentForm.estimatedCost} onChange={(e) => setIncidentForm((current) => ({ ...current, estimatedCost: e.target.value }))} placeholder="Beklenen maliyet" className="w-full border rounded-xl px-3 py-3" />
              <input type="number" step="0.01" value={incidentForm.invoiceAmount} onChange={(e) => setIncidentForm((current) => ({ ...current, invoiceAmount: e.target.value }))} placeholder="Fatura tutarı" className="w-full border rounded-xl px-3 py-3" />
            </div>
            <input value={incidentForm.invoiceNumber} onChange={(e) => setIncidentForm((current) => ({ ...current, invoiceNumber: e.target.value }))} placeholder="Fatura No" className="w-full border rounded-xl px-3 py-3" />
            <textarea value={incidentForm.description} onChange={(e) => setIncidentForm((current) => ({ ...current, description: e.target.value }))} rows={5} placeholder="Açıklama" className="w-full border rounded-xl px-3 py-3 resize-none" />
            <label className="block border-2 border-dashed rounded-2xl border-slate-300 p-4 text-center text-sm text-slate-600 cursor-pointer">Fatura görseli seç<input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setIncidentInvoiceFile(e.target.files?.[0] || null)} />{incidentInvoiceFile ? <div className="mt-2 text-emerald-700 font-medium">{incidentInvoiceFile.name}</div> : null}</label>
            <label className="block border-2 border-dashed rounded-2xl border-slate-300 p-4 text-center text-sm text-slate-600 cursor-pointer">Kaza/Arıza görselleri seç<input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setIncidentImageFiles(Array.from(e.target.files || []))} />{incidentImageFiles.length > 0 ? <div className="mt-2 text-emerald-700 font-medium">{incidentImageFiles.length} görsel seçildi</div> : null}</label>
            <button onClick={submitIncident} disabled={savingIncident} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-rose-600 text-white py-3 font-medium disabled:bg-rose-300 sticky bottom-0">{savingIncident ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}Kaydı Gönder</button>
          </div>
        </div>
      )}

      <DriverBottomNav />
    </div>
  );
}
